// Helpers for the Austrian client's desired insurance selection, stored as a
// compact JSON in the `clientCoveragesInfo` field (no native backend fields):
//   {
//     "requestedLines": {"mtpl":true,"hull":true,"hullVariant":"VOLLKASKO",...},
//     "coverages": {"gap":true,"replacementValue":false,...}
//   }
// The additional coverages (gap / replacement value / gross negligence /
// electro plus package) have NO backend columns, so they live in this JSON too.

export type HullVariant = "TEILKASKO" | "VOLLKASKO";

export interface RequestedLines {
  mtpl: boolean;
  hull: boolean;
  legal: boolean;
  accident: boolean;
  assistance: boolean;
  // Hull sub-type (only meaningful when hull = true).
  hullVariant?: HullVariant | null;
  // CZ only: requested coverage period per line, "YYYY-MM-DD" (as in LT).
  // Omitted from the JSON when empty, so the AT JSON stays unchanged.
  mtplCoverageFrom?: string;
  mtplCoverageTo?: string;
  hullCoverageFrom?: string;
  hullCoverageTo?: string;
  // CZ only: the MTPL insurance limit the client picked, "{personal injury}/
  // {property damage}" (CZK), from the main MTPL insurer's limits applicable on
  // mtplCoverageFrom (see utils/mtplInsuranceLimits). Omitted when not picked.
  mtplInsuranceLimit?: string;
  // CZ only: the hull deductible option the client picked,
  // "{percent}%/min: {min} CZK/max: {max} CZK" (without the undefined parts),
  // from the main Hull insurer's options applicable on hullCoverageFrom (see
  // utils/hullDeductibleOptions). Omitted when not picked.
  hullDeductibleOption?: string;
  // CZ only: the assistance option the client picked (a text as defined in
  // the "assistanceOptions" setting of the main insurer of the row it is
  // shown in, see resolveAssistanceOptions). Omitted when not picked.
  assistanceOption?: string;
  // CZ only: windscreen coverage (offered with MTPL or Hull), its scope and
  // limit (whole CZK). Scope / limit omitted when not set.
  windscreen?: boolean;
  windscreenScope?: WindscreenScope;
  windscreenLimit?: number;
}

export type WindscreenScope = "WINDSCREEN_ONLY" | "ALL_WINDOWS";

export const WINDSCREEN_SCOPES: { key: WindscreenScope; labelKey: string }[] = [
  {
    key: "WINDSCREEN_ONLY",
    labelKey: "bfm.vehicleForm.windscreenScope.windscreenOnly",
  },
  { key: "ALL_WINDOWS", labelKey: "bfm.vehicleForm.windscreenScope.allWindows" },
];

// Formik error keys of the windscreen scope / limit (shown under their controls;
// also their ids / input names).
export const WINDSCREEN_SCOPE_ERROR_KEY = "clientCoveragesInfo_windscreenScope";
export const WINDSCREEN_LIMIT_ERROR_KEY = "clientCoveragesInfo_windscreenLimit";

/**
 * CZ: the requested windscreen coverage as displayed everywhere (offer summary,
 * vehicle list column): "Windscreen (Windscreen only, limit: 20000 CZK)"; null
 * when not requested. The backend history text uses the same order.
 */
export const formatWindscreen = (
  lines: RequestedLines,
  t: (key: string) => string
): string | null => {
  if (!lines.windscreen) return null;
  const details: string[] = [];
  const scope = WINDSCREEN_SCOPES.find((s) => s.key === lines.windscreenScope);
  if (scope) details.push(t(scope.labelKey));
  if (lines.windscreenLimit != null) {
    details.push(
      `${t("bfm.vehicleForm.windscreenLimit.short")}: ${lines.windscreenLimit} CZK`
    );
  }
  const label = t("bfm.vehicleForm.windscreen.label");
  return details.length > 0 ? `${label} (${details.join(", ")})` : label;
};

// Lines that carry a requested coverage period (CZ).
export const COVERAGE_DATE_LINES = ["mtpl", "hull"] as const;
export type CoverageDateLine = typeof COVERAGE_DATE_LINES[number];
export type CoverageDateKey =
  | "mtplCoverageFrom"
  | "mtplCoverageTo"
  | "hullCoverageFrom"
  | "hullCoverageTo";

export const coverageDateKeys = (
  line: CoverageDateLine
): { from: CoverageDateKey; to: CoverageDateKey } => ({
  from: `${line}CoverageFrom` as CoverageDateKey,
  to: `${line}CoverageTo` as CoverageDateKey,
});

// Formik error key of a coverage date (shown under its date picker).
export const coverageDateErrorKey = (key: CoverageDateKey) =>
  `clientCoveragesInfo_${key}`;

// CZ: per line with a coverage period, the option the client picks from the
// fleet's default setting of the line's main insurer (shown as a drop-down next
// to the line's coverage dates): MTPL insurance limit, hull deductible option.
export type LineOptionKey = "mtplInsuranceLimit" | "hullDeductibleOption";
export const LINE_OPTIONS: Record<
  CoverageDateLine,
  {
    key: LineOptionKey;
    labelKey: string;
    placeholderKey: string;
    // label of the option in the requested line's details ("limit: 100/100")
    shortLabelKey: string;
  }
> = {
  mtpl: {
    key: "mtplInsuranceLimit",
    labelKey: "bfm.vehicleForm.mtplInsuranceLimit.label",
    placeholderKey: "bfm.vehicleForm.mtplInsuranceLimit.placeholder",
    shortLabelKey: "bfm.vehicleForm.mtplInsuranceLimit.short",
  },
  hull: {
    key: "hullDeductibleOption",
    labelKey: "bfm.vehicleForm.hullDeductibleOption.label",
    placeholderKey: "bfm.vehicleForm.hullDeductibleOption.placeholder",
    shortLabelKey: "bfm.vehicleForm.hullDeductibleOption.short",
  },
};

/** CZ: the options offered for a line requested from a coverage start. */
export type LineOptionsGetters = Partial<
  Record<CoverageDateLine, (coverageFrom?: string) => string[]>
>;

// Formik error key of a line's option (shown under its drop-down; also the
// drop-down's id, so the error notification can scroll to it).
export const lineOptionErrorKey = (key: LineOptionKey) =>
  `clientCoveragesInfo_${key}`;

// Formik error key of the assistance option (shown under its drop-down; also
// the drop-down's id).
export const ASSISTANCE_OPTION_ERROR_KEY = "clientCoveragesInfo_assistanceOption";

/**
 * CZ: the assistance options offered for the requested Assistance and the
 * line whose row shows their drop-down (only one): the first of MTPL, Hull
 * that is requested with a coverage start and whose main insurer has
 * assistance options for that day. null when Assistance isn't requested or no
 * such line exists.
 */
export const resolveAssistanceOptions = (
  lines: RequestedLines,
  getAssistanceOptions: LineOptionsGetters | undefined
): { line: CoverageDateLine; options: string[] } | null => {
  if (!lines.assistance || !getAssistanceOptions) return null;
  for (const line of COVERAGE_DATE_LINES) {
    const from = lines[coverageDateKeys(line).from];
    const getOptions = getAssistanceOptions[line];
    if (!lines[line] || !from || !getOptions) continue;
    const options = getOptions(from);
    if (options.length > 0) return { line, options };
  }
  return null;
};

/**
 * Label of an error of a clientCoveragesInfo part (coverage date, line option),
 * for the form's error notifications - these errors have no vehicle field and
 * no "bfm.vehicleForm.{error}.label" translation. undefined for other errors.
 */
export const clientCoveragesErrorLabel = (
  errorKey: string,
  t: (key: string) => string
): string | undefined => {
  for (const line of COVERAGE_DATE_LINES) {
    const lineLabelKey = REQUESTED_LINE_FIELDS.find(
      (f) => f.key === line
    )?.labelKey;
    const lineLabel = lineLabelKey ? t(lineLabelKey) : line;
    const { from, to } = coverageDateKeys(line);
    if (errorKey === coverageDateErrorKey(from))
      return `${lineLabel} - ${t("bfm.CoverageFrom.label")}`;
    if (errorKey === coverageDateErrorKey(to))
      return `${lineLabel} - ${t("bfm.CoverageTo.label")}`;
    if (errorKey === lineOptionErrorKey(LINE_OPTIONS[line].key))
      return t(LINE_OPTIONS[line].labelKey);
  }
  if (errorKey === ASSISTANCE_OPTION_ERROR_KEY)
    return t("bfm.vehicleForm.assistanceOption.label");
  if (errorKey === WINDSCREEN_SCOPE_ERROR_KEY)
    return `${t("bfm.vehicleForm.windscreen.label")} - ${t(
      "bfm.vehicleForm.windscreenScope.label"
    )}`;
  if (errorKey === WINDSCREEN_LIMIT_ERROR_KEY)
    return `${t("bfm.vehicleForm.windscreen.label")} - ${t(
      "bfm.vehicleForm.windscreenLimit.label"
    )}`;
  return undefined;
};

// Local date <-> "YYYY-MM-DD" without timezone shifts.
export const serializeCoverageDate = (
  date?: Date | null
): string | undefined => {
  if (!date) return undefined;
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const parseCoverageDate = (value?: string | null): Date | undefined => {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : undefined;
};

// CZ: the client's requested coverage start of a line (MTPL / Hull), if any.
export const getRequestedCoverageFrom = (
  coveragesInfo: string | null | undefined,
  line: CoverageDateLine
): Date | undefined =>
  parseCoverageDate(
    parseRequestedLines(coveragesInfo)[coverageDateKeys(line).from]
  );

// "YYYY-MM-DD" -> "dd.MM.yyyy" (display); unparsable values are shown as they are.
export const formatCoverageDate = (value?: string | null): string => {
  const date = parseCoverageDate(value);
  if (!date) return value ?? "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${date.getFullYear()}`;
};

// "01.11.2026 - 31.12.2026" for a line with a requested period, otherwise "".
export const formatCoveragePeriod = (
  lines: RequestedLines,
  line: CoverageDateLine
): string => {
  const { from, to } = coverageDateKeys(line);
  if (!lines[from] && !lines[to]) return "";
  return `${formatCoverageDate(lines[from])} - ${formatCoverageDate(lines[to])}`;
};

// A requested line as displayed everywhere (offer summary, vehicle list column):
// "HULL (Full, 01.11.2026 - 31.12.2026, deductible: 10%/min: 5000 CZK)",
// "MTPL (01.11.2026 - 31.12.2026, limit: 100/100)", "ASSISTANCE (SUPER)".
// The backend history text (GetRequestedCoverageInfo) uses the same order.
export const formatRequestedLine = (
  lines: RequestedLines,
  key: keyof RequestedLines,
  t: (key: string) => string
): string => {
  const details: string[] = [];
  if (key === "hull" && lines.hullVariant) {
    details.push(
      t(
        lines.hullVariant === "VOLLKASKO"
          ? "bfm.vehicleForm.hullVariant.vollkasko"
          : "bfm.vehicleForm.hullVariant.teilkasko"
      )
    );
  }
  // CZ: requested coverage period
  if (key === "mtpl" || key === "hull") {
    const period = formatCoveragePeriod(lines, key);
    if (period) details.push(period);
  }
  // CZ: picked MTPL insurance limit / hull deductible option
  if (key === "mtpl" || key === "hull") {
    const option = LINE_OPTIONS[key];
    const value = lines[option.key];
    if (value) details.push(`${t(option.shortLabelKey)}: ${value}`);
  }
  // CZ: picked assistance option, as it is defined
  if (key === "assistance" && lines.assistanceOption) {
    details.push(lines.assistanceOption);
  }
  const labelKey = REQUESTED_LINE_FIELDS.find((f) => f.key === key)?.labelKey;
  const label = labelKey ? t(labelKey) : String(key);
  return details.length > 0 ? `${label} (${details.join(", ")})` : label;
};

export const EMPTY_REQUESTED_LINES: RequestedLines = {
  mtpl: false,
  hull: false,
  legal: false,
  accident: false,
  assistance: false,
  hullVariant: null,
};

// Order + labels reuse the existing PremiumType.* translation keys.
export const REQUESTED_LINE_FIELDS: {
  key: keyof RequestedLines;
  labelKey: string;
}[] = [
  { key: "mtpl", labelKey: "PremiumType.MTPL" },
  { key: "hull", labelKey: "PremiumType.HULL" },
  { key: "legal", labelKey: "PremiumType.LEGAL" },
  { key: "accident", labelKey: "PremiumType.ACCIDENT" },
  { key: "assistance", labelKey: "PremiumType.ASSISTANCE" },
];

// Assistance can't exist without MTPL or Hull: it is offered only when one of
// them is requested.
export const isRequestedLineAvailable = (
  key: keyof RequestedLines,
  lines: RequestedLines
): boolean => key !== "assistance" || lines.mtpl || lines.hull;

// CZ client: Legal / Accident are not offered. The full list above still
// labels older requests in the read-only summary.
export const CZ_REQUESTED_LINE_FIELDS = REQUESTED_LINE_FIELDS.filter(
  (f) => f.key !== "legal" && f.key !== "accident"
);

// Additional hull coverages the client can request. These have NO backend
// columns, so — like the requested lines — they live inside `clientCoveragesInfo`.
export interface Coverages {
  gap: boolean;
  replacementValue: boolean;
  grossNegligence: boolean;
  electroPlusPackage: boolean;
}

export const EMPTY_COVERAGES: Coverages = {
  gap: false,
  replacementValue: false,
  grossNegligence: false,
  electroPlusPackage: false,
};

export const COVERAGE_FIELDS: { key: keyof Coverages; labelKey: string }[] = [
  { key: "gap", labelKey: "bfm.vehicleForm.gap.label" },
  { key: "replacementValue", labelKey: "bfm.vehicleForm.replacementValue.label" },
  { key: "grossNegligence", labelKey: "bfm.vehicleForm.grossNegligence.label" },
  {
    key: "electroPlusPackage",
    labelKey: "bfm.vehicleForm.electroPlusPackage.label",
  },
];

export const parseRequestedLines = (
  coveragesInfo?: string | null
): RequestedLines => {
  if (!coveragesInfo) return { ...EMPTY_REQUESTED_LINES };
  try {
    const parsed = JSON.parse(coveragesInfo);
    return { ...EMPTY_REQUESTED_LINES, ...(parsed?.requestedLines || {}) };
  } catch {
    return { ...EMPTY_REQUESTED_LINES };
  }
};

export const parseCoverages = (coveragesInfo?: string | null): Coverages => {
  if (!coveragesInfo) return { ...EMPTY_COVERAGES };
  try {
    const parsed = JSON.parse(coveragesInfo);
    return { ...EMPTY_COVERAGES, ...(parsed?.coverages || {}) };
  } catch {
    return { ...EMPTY_COVERAGES };
  }
};

// Serialize both the requested lines and the additional coverages into the
// single `clientCoveragesInfo` JSON. Writers must pass both so neither half is
// dropped when only one is edited.
export const serializeClientCoverages = (
  lines: RequestedLines,
  coverages: Coverages
): string => JSON.stringify({ requestedLines: lines, coverages });
